import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import ProtectedRoute from '../auth/ProtectedRoute';
import FeatureProtectedRoute from '../auth/FeatureProtectedRoute';
import AuthenticatedLayout from './AuthenticatedLayout';
import SuspenseFallback from './SuspenseFallback';
import {
  Settings,
  Groups,
  UsersManagement,
  ThemeAndLogo,
  ConfigurationManagement,
} from './LazyLoadedComponents';

// Regular imports
import TenantSelection from '../auth/TenantSelection';
import Login from '../auth/Login';
import AuthCallback from '../auth/AuthCallback';
import PromptManagerDashboard from '../PromptManagerDashboard/index';
import TemplateDashboard from '../TemplateDashboard/index';
import TemplateEditor from '../TemplateEditor';
import VersionManager from '../VersionManager/index';
import ExecutionTester from '../ExecutionTester/ExecutionTester';
import ExecutionManager from '../ExecutionManager/ExecutionManager';
import ExperimentSection from '../ExperimentManager/ExperimentSection';
import { AnalyticsDashboard, EntityInformation } from '../Governance';
import ContentCheckAuditDashboard from '../Governance/ContentCheckAuditDashboard';
import SupportedPolicies from '../Governance/SupportedPolicies';
import Analytics from '../AnalyticsInsights/Analytics';
import Insights from '../AnalyticsInsights/Insights';
import ContentCheckPolicyManager from '../ContentCheckPolicyManager/ContentCheckPolicyManager';
import ThemeManagement from '../ThemeManagement';
import StaticLandingPage from '../landing/StaticLandingPage';

/**
 * RouteConfig Component
 * Centralized route configuration for the application
 */
const RouteConfig = ({ navigationOpen, onToggleNavigation }) => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/tenant-selection" element={<TenantSelection />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes with navigation */}
      <Route
        path="/prompt-dashboard"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <PromptManagerDashboard />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <TemplateDashboard />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/new"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <TemplateEditor />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/edit"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <TemplateEditor />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/content-check"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ContentCheckPolicyManager />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/versions"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <VersionManager />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/versions/:versionNumber/content-check"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ContentCheckPolicyManager />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/versions/:versionId/edit"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <TemplateEditor />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/:templateId/execute"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExecutionTester />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Execution Management Routes */}
      <Route
        path="/executions"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExecutionManager />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/executions/experiments"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExecutionManager />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/experiments"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExperimentSection />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/experiments/create"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExperimentSection />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/experiments/results"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ExperimentSection />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/governance"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="GOVERNANCE_COMPLIANCE"
                featureName="Governance & Compliance"
              >
                <Navigate to="/governance/dashboard" replace />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/governance/dashboard"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="GOVERNANCE_COMPLIANCE"
                featureName="Governance & Compliance"
              >
                <AnalyticsDashboard />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/governance/audit-logs"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="GOVERNANCE_COMPLIANCE"
                featureName="Governance & Compliance"
              >
                <ContentCheckAuditDashboard />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/governance/policies"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="GOVERNANCE_COMPLIANCE"
                featureName="Governance & Compliance"
              >
                <SupportedPolicies />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/governance/entities"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="GOVERNANCE_COMPLIANCE"
                featureName="Governance & Compliance"
              >
                <EntityInformation />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Analytics & Insights routes */}
      <Route
        path="/analytics-insights"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="ANALYTICS_INSIGHTS"
                featureName="Analytics & Insights"
              >
                <Navigate to="/analytics-insights/analytics" replace />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics-insights/analytics"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="ANALYTICS_INSIGHTS"
                featureName="Analytics & Insights"
              >
                <Analytics />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics-insights/insights"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <FeatureProtectedRoute
                requiredFeature="ANALYTICS_INSIGHTS"
                featureName="Analytics & Insights"
              >
                <Insights />
              </FeatureProtectedRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings route - Lazy loaded for admin/owner only */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <Suspense fallback={<SuspenseFallback />}>
                <Settings />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Users Management route - Lazy loaded for admin/owner only, nested under Settings */}
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <Suspense fallback={<SuspenseFallback />}>
                <UsersManagement />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Theme & Logo route - Lazy loaded for admin/owner only, nested under Settings */}
      <Route
        path="/settings/theme-logo"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <Suspense fallback={<SuspenseFallback />}>
                <ThemeAndLogo />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Groups route - Lazy loaded for admin/owner only, nested under Settings */}
      <Route
        path="/settings/groups"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <Suspense fallback={<SuspenseFallback />}>
                <Groups />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Configuration Management route - Lazy loaded for admin/owner only, nested under Settings */}
      <Route
        path="/settings/configuration"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <Suspense fallback={<SuspenseFallback />}>
                <ConfigurationManagement />
              </Suspense>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirect old /groups route to /settings/groups for backward compatibility */}
      <Route
        path="/groups"
        element={<Navigate to="/settings/groups" replace />}
      />

      {/* Theme Management route */}
      <Route
        path="/theme-management"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout
              navigationOpen={navigationOpen}
              onToggleNavigation={onToggleNavigation}
            >
              <ThemeManagement />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Landing page - redirect to prompt-dashboard if authenticated */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/prompt-dashboard" replace />
          ) : (
            <StaticLandingPage />
          )
        }
      />

      {/* Catch all route - redirect to prompt-dashboard if authenticated, otherwise to landing */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to="/prompt-dashboard" replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </Routes>
  );
};

RouteConfig.propTypes = {
  navigationOpen: PropTypes.bool.isRequired,
  onToggleNavigation: PropTypes.func.isRequired,
};

export default RouteConfig;
